import type { HttpClient } from '@/infrastructure/http/HttpClient'
import type {
  RenditionReviewRepository,
  ReviewLinePatch,
  NewReviewLine,
  ReviewInvoicePatch,
  AccountingCatalogs,
  LineSplitsInput,
} from '@/domain/renditionReview/RenditionReviewRepository'
import type {
  RenditionReviewDetail,
  ReviewInvoice,
  ReviewLine,
  LineDecision,
} from '@/domain/renditionReview/RenditionReview'
import type { LineTax, AccountingSplit, RenditionInvoice } from '@/domain/rendition/Rendition'
import { mapInvoice } from '@/infrastructure/rendition/renditionPayload'
import { customFieldAnswersFromApi } from '@/application/rendition/splitMapping'

/**
 * HTTP adapter for the reviewer flow, wired to the real backend (finnecto-api
 * `feat/rendiciones-07-line-approval`). Endpoints copy the payment approval flow:
 * - READ:   `GET purchases/requests/{id}/` — the rendition comes under `items` (same shape as the
 *           rendition create/draft retrieve: nested `tax_details` + `splits`). Amounts are NATIVE
 *           per invoice + `exchange_rate`/`currency`; the FE converts (decision B). Each item carries
 *           `product_line_id` (the approval key) besides `id` (the invoice id, used for edits).
 * - SUBMIT: approval is per boleta, handled by `RequestApproval.vue` (payment flow): `PUT
 *           purchases/requests/{id}/` with `type="REVIEW"`, `node` and `product_lines:[{id,status}]`,
 *           keyed by `product_line_id`. Not per line — `invoice_lines` was removed on both ends.
 * - EDIT:   `POST purchases/requests/{id}/actions/` `action="EDIT_LINE"` (R5, rendition-only guard).
 * Mutations re-fetch the READ so the FE renders from server truth.
 * Return-to-requester lives in the approval flow (Aprobaciones tab), not here.
 */

interface RawRef {
  id: number
  name: string
  code?: string
}

interface RawTaxDetail {
  tax_name: string
  tax_type?: string
  percentage: number
  taxable_amount: number
  tax_amount: number
}

interface RawSplit {
  id?: number
  cost_center: RawRef | null
  account: RawRef | null
  amount?: number
  percentage: number
  custom_field_answers?: { custom_field_id: number; value: string }[]
}

interface RawWithholding {
  tax_type?: string | null
  tax_type_code?: string | null
  percentage?: number | null
  taxable_amount?: number | null
  tax_amount?: number | null
}

interface RawLine {
  id: number
  product?: string
  description?: string
  total: number
  // Discount percentage applied to the line (the backend recomputes the net from it).
  discount?: number | null
  quantity?: number | null
  unit_price?: number | null
  tax_details?: RawTaxDetail[]
  withholdings?: RawWithholding[]
  splits?: RawSplit[]
}

interface RawItem {
  id: number
  // ProductLine id of the boleta — the key for approve/reject (`product_lines:[{id,status}]`).
  product_line_id?: number
  number: string
  // Fiscal document type. Not shipped by the read yet — see ReviewInvoice.type.
  type?: string | null
  provider?: { id: number; name: string } | null
  category?: RawRef | null
  subcategory?: RawRef | null
  area?: RawRef | null
  emission_date?: string
  currency: string
  exchange_rate?: number
  converted_total?: number
  total: number
  decision?: LineDecision
  // Name of the approver who rejected the boleta; only present on a REJECTED one.
  rejector?: string | null
  document_url?: string
  document_name?: string
  lines?: RawLine[]
  shared?: { people: Array<{ name: string }>; editable_in_rendition?: boolean; part?: { amount?: number | string; splits?: RawSplit[] } | null } | null
  duplicate_alert_count?: number
}

interface RawReviewDetail {
  id: number
  status: string
  is_current_approver?: boolean
  pending_actions?: number
  total_actions?: number
  current_approver_node_id?: number | null
  items?: RawItem[]
}

function mapTax(t: RawTaxDetail): LineTax {
  return {
    taxName: t.tax_name,
    taxPercentage: t.percentage,
    taxableAmount: t.taxable_amount,
    taxAmount: t.tax_amount,
    taxTypeCode: (t.tax_type ?? '').toUpperCase().includes('WITH') ? 'WITHHOLDING' : 'TAX',
  }
}

// Withholdings carry the name in `tax_type`, and their amounts may be null when the backend
// hasn't computed them yet — normalise to the same LineTax shape the taxes use.
function mapWithholding(w: RawWithholding): LineTax {
  return {
    taxName: w.tax_type ?? w.tax_type_code ?? '',
    taxPercentage: w.percentage ?? 0,
    taxableAmount: w.taxable_amount ?? 0,
    taxAmount: w.tax_amount ?? 0,
    taxTypeCode: 'WITHHOLDING',
  }
}

function mapSplit(s: RawSplit): AccountingSplit {
  return {
    costCenterId: s.cost_center?.id,
    costCenterLabel: s.cost_center?.name ?? '',
    accountId: s.account?.id,
    accountLabel: s.account?.name ?? '',
    accountCode: s.account?.code,
    customFieldsAnswers: customFieldAnswersFromApi(s.custom_field_answers),
    percentage: s.percentage,
  }
}

function hydrateLine(raw: RawLine): ReviewLine {
  return {
    id: raw.id,
    itemName: raw.product ?? '',
    description: raw.description,
    taxes: (raw.tax_details ?? []).map(mapTax),
    // Un withholding "vacío" —solo `tax_type`, con percentage/tax_amount/taxable_amount en null— es una
    // sugerencia de la política que el read incluye pero la línea NO tiene aplicada: no debe pintar chip.
    withholdings: (raw.withholdings ?? [])
      .filter((w) => w.percentage != null || w.tax_amount != null || w.taxable_amount != null)
      .map(mapWithholding),
    discount: raw.discount ?? undefined,
    quantity: raw.quantity ?? 1,
    unitPrice: raw.unit_price ?? raw.total,
    splits: (raw.splits ?? []).map(mapSplit),
    total: raw.total,
  }
}

function hydrateItem(raw: RawItem): ReviewInvoice {
  return {
    id: raw.id,
    productLineId: raw.product_line_id,
    // The id as well as the name — `EDIT_INVOICE` identifies the provider by id.
    providerId: raw.provider?.id,
    providerName: raw.provider?.name ?? '',
    number: raw.number,
    duplicateAlertCount: raw.duplicate_alert_count ?? 0,
    documentUrl: raw.document_url,
    documentName: raw.document_name,
    categoryId: raw.category?.id,
    subcategoryId: raw.subcategory?.id,
    areaId: raw.area?.id,
    categoryLabel: raw.category?.name,
    subcategoryLabel: raw.subcategory?.name,
    areaLabel: raw.area?.name,
    emissionDate: raw.emission_date,
    type: raw.type ?? undefined,
    currency: raw.currency,
    exchangeRate: raw.exchange_rate ?? undefined,
    // Backend conversion into the policy currency. Falls back to the native total only when the backend
    // did not send one, which (same-currency aside) should not happen once both deploy in lockstep.
    convertedTotal: raw.converted_total ?? raw.total,
    total: raw.total,
    decision: raw.decision ?? 'TO_REVIEW',
    rejector: raw.rejector ?? undefined,
    lines: (raw.lines ?? []).map(hydrateLine),
    ...(raw.shared
      ? {
        shared: {
          people: raw.shared.people.map((p) => p.name),
          documentLocked: raw.shared.editable_in_rendition === false,
          partAmount: Number(raw.shared.part?.amount ?? 0),
          partSplits: (raw.shared.part?.splits ?? []).map(mapSplit),
        },
      }
      : {}),
  }
}

function hydrate(raw: RawReviewDetail): RenditionReviewDetail {
  // Approval is per boleta now. Decided boletas stay visible with their decision chip (like payments):
  // APPROVED/REJECTED render read-only, only TO_REVIEW boletas keep the approval checkbox. Lines carry
  // no decision anymore, so there's no per-line rejection filtering. A REJECTED boleta only arrives
  // because `getForReview` asks for it — the draft read (carousel, resubmit) leaves it out.
  const invoices = (raw.items ?? []).map(hydrateItem)

  return {
    id: raw.id,
    reference: String(raw.id),
    status: raw.status,
    // Metadata (policy/area/requester/beneficiary/…) is rendered by the existing RequestDetails
    // chrome, not this read — the reviewer surface only needs the líneas breakdown + approval turn.
    policyName: '',
    requesterName: '',
    beneficiaryName: '',
    paymentType: '',
    currency: '',
    totalRequested: 0,
    isCurrentApprover: raw.is_current_approver ?? false,
    pendingActions: raw.pending_actions ?? 0,
    totalActions: raw.total_actions ?? 0,
    currentApproverNodeId: raw.current_approver_node_id ?? undefined,
    accountingByLine: true,
    invoices,
  }
}

export class HttpRenditionReviewRepository implements RenditionReviewRepository {
  constructor(private http: HttpClient) {}

  // `include_rejected`: the read hides rejected boletas by default because the same endpoint seeds the
  // edit/resubmit form, where a rejected boleta would be resubmitted. Only the reviewer surface asks.
  async getForReview(id: number): Promise<RenditionReviewDetail> {
    const raw = await this.http
      .url(`purchases/requests/${id}`)
      .params({ include_rejected: 'true' })
      .get<RawReviewDetail>()
    return hydrate(raw)
  }

  // R5 — inline amount / accounting correction (rendition-only guard, backend-side). Splits go in
  // snake_case (`cost_center`/`account`/`percentage`); the server recomputes `amount`. Re-fetch.
  async updateLine(
    id: number,
    lineId: number,
    patch: ReviewLinePatch,
  ): Promise<RenditionReviewDetail> {
    await this.http
      .url(`purchases/requests/${id}/actions`)
      .body({
        action: 'EDIT_LINE',
        line: lineId,
        // El FE manda todo — unit_price, quantity, discount (en la unidad de la boleta) y tax_details
        // recalculados — igual que al crear la línea; el backend recalcula `total` (ver ReviewLinePatch).
        // `item`/`splits` opcionales; retenciones fuera de alcance.
        item: patch.itemId,
        unit_price: patch.unitPrice,
        quantity: patch.quantity,
        discount: patch.discount,
        total: patch.total,
        // Reemplaza TODO el set de impuestos de la línea (no hacen falta ids).
        tax_details: patch.taxes?.map((t) => ({
          tax_name: t.taxName,
          percentage: t.taxPercentage,
          taxable_amount: t.taxableAmount,
          tax_amount: t.taxAmount,
        })),
        splits: patch.splits?.map((s) => ({
          cost_center: s.costCenterId,
          account: s.accountId,
          percentage: s.percentage,
          custom_fields_answers: s.customFieldsAnswers ?? {},
        })),
      })
      .post<unknown>()
    return this.getForReview(id)
  }

  // ADD_LINE — alta de una línea en una boleta (alcance del revisor, guard RENDITION en backend).
  async addLine(id: number, line: NewReviewLine): Promise<RenditionReviewDetail> {
    await this.http
      .url(`purchases/requests/${id}/actions`)
      .body({
        action: 'ADD_LINE',
        invoice: line.invoiceId,
        item: line.itemId,
        total: line.total,
        splits: line.splits?.map((s) => ({
          cost_center: s.costCenterId,
          account: s.accountId,
          percentage: s.percentage,
          custom_fields_answers: s.customFieldsAnswers ?? {},
        })),
      })
      .post<unknown>()
    return this.getForReview(id)
  }

  async updateShareSplits(id: number, productLineId: number, splits: AccountingSplit[]): Promise<RenditionReviewDetail> {
    await this.http
      .url(`purchases/requests/${id}/actions`)
      .body({
        action: 'EDIT_SHARE_SPLITS',
        product_line: productLineId,
        splits: splits.map((s) => ({
          cost_center: s.costCenterId,
          account: s.accountId,
          percentage: s.percentage,
          custom_fields_answers: s.customFieldsAnswers ?? {},
        })),
      })
      .post()
    return this.getForReview(id)
  }

  // EDIT_INVOICE — cabecera de la boleta. Partial update: solo van los campos presentes, así que
  // se arma el body filtrando `undefined` (mandar `null` borraría el valor en el backend).
  async updateInvoice(
    id: number,
    invoiceId: number,
    patch: ReviewInvoicePatch,
  ): Promise<RenditionReviewDetail> {
    const body: Record<string, unknown> = { action: 'EDIT_INVOICE', invoice: invoiceId }
    if (patch.providerId !== undefined) body.provider = patch.providerId
    else if (patch.providerName !== undefined) body.provider_name = patch.providerName
    if (patch.number !== undefined) body.number = patch.number
    if (patch.type !== undefined) body.type = patch.type
    if (patch.emissionDate !== undefined) body.emission_date = patch.emissionDate
    if (patch.currency !== undefined) body.currency = patch.currency
    if (patch.categoryId !== undefined) body.category = patch.categoryId
    if (patch.subcategoryId !== undefined) body.subcategory = patch.subcategoryId
    if (patch.areaId !== undefined) body.area = patch.areaId
    await this.http.url(`purchases/requests/${id}/actions`).body(body).post<unknown>()
    return this.getForReview(id)
  }

  /**
   * "Guardar" del carrusel: la boleta COMPLETA en un request, con la misma forma que arma la creación
   * (`mapInvoice`). Las líneas van sin id porque el backend REEMPLAZA el set — por eso una línea que el
   * revisor sacó viaja simplemente ausente, y no hace falta una acción de borrado.
   */
  async saveInvoiceFull(
    id: number,
    invoice: RenditionInvoice,
    options?: { decimalsFor?: (currency: string) => number },
  ): Promise<RenditionReviewDetail> {
    const decimalsFor = options?.decimalsFor ?? (() => 0)
    await this.http
      .url(`purchases/requests/${id}/actions`)
      .body({ action: 'EDIT_INVOICE', invoice: invoice.id, ...mapInvoice(invoice, decimalsFor) })
      .post<unknown>()
    return this.getForReview(id)
  }

  // Accounting-matrix combination selects reuse the platform's splits form (cost centers + accounts).
  async loadAccountingCatalogs(): Promise<AccountingCatalogs> {
    const raw = await this.http.url('purchases/splits/form').get<{
      cost_centers?: { id: number; name: string; code?: string }[]
      accounts?: { id: number; name: string; code?: string }[]
      splittable_custom_fields?: { id: number; name: string; options?: string[]; default_option?: string }[]
    }>()
    return {
      costCenters: (raw.cost_centers ?? []).map((c) => ({ id: c.id, name: c.name, code: c.code })),
      accounts: (raw.accounts ?? []).map((a) => ({ id: a.id, name: a.name, code: a.code })),
      customFields: (raw.splittable_custom_fields ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        options: f.options ?? [],
        defaultOption: f.default_option,
      })),
    }
  }

  // Bulk save of the accounting matrix. `EDIT_LINES_SPLITS` replaces each listed line's splits in one
  // transaction (each line must sum 100% or 400; `[]` = unassigned). It returns the refreshed splits
  // per line (NOT the timeline) — splits don't affect the strip totals, so no re-fetch; the store
  // merges these into the current detail.
  async updateLinesSplits(id: number, changes: LineSplitsInput[]): Promise<LineSplitsInput[]> {
    const res = await this.http
      .url(`purchases/requests/${id}/actions`)
      .body({
        action: 'EDIT_LINES_SPLITS',
        lines: changes.map((c) => ({
          line: c.lineId,
          splits: c.splits.map((s) => ({
            cost_center: s.costCenterId,
            account: s.accountId,
            percentage: s.percentage,
            custom_fields_answers: s.customFieldsAnswers ?? {},
          })),
        })),
      })
      .post<{ action?: { lines?: Array<{ line: number; splits?: RawSplit[] }> } }>()
    return (res.action?.lines ?? []).map((l) => ({
      lineId: l.line,
      splits: (l.splits ?? []).map(mapSplit),
    }))
  }
}
